<template>
  <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div class="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
      <div class="flex items-center gap-1.5">
        <font-awesome-icon :icon="['fas', listIcon]" :class="iconColor" class="text-[10px]" />
        <h3 class="text-xs font-semibold text-gray-900">{{ displayTitle }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span v-if="contractId" class="sensitive text-[10px] text-gray-400" data-spoof-kind="contract">{{ contractId }}</span>
        <span v-if="loading" class="text-[10px] text-gray-400">Loading...</span>
      </div>
    </div>

    <div v-if="!loading && !displayEntries.length" class="px-3 py-4 text-center text-gray-400 text-xs">No data available.</div>

    <table v-else-if="displayEntries.length" class="w-full text-xs table-fixed">
      <colgroup>
        <col class="w-6" />
        <col style="width: 16%" />
        <col class="w-12" />
        <col style="width: 22%" />
        <col style="width: 24%" />
        <col class="w-14" />
        <col class="w-10" />
      </colgroup>
      <thead>
        <tr class="text-[10px] text-gray-400 uppercase tracking-wider border-b border-gray-100">
          <th class="pl-3 pr-2 py-1.5 text-left font-medium">#</th>
          <th class="px-2 py-1.5 text-left font-medium">Practice</th>
          <th class="px-2 py-1.5 text-right font-medium">Panel</th>
          <th class="px-2 py-1.5 text-left font-medium">Context</th>
          <th class="px-2 py-1.5 text-left font-medium">Analysis</th>
          <th class="px-2 py-1.5 text-center font-medium">Trend</th>
          <th class="pr-3 pl-2 py-1.5 text-right font-medium">Score</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-50">
        <template v-for="(entry, idx) in displayEntries" :key="entry.entity_id">
          <tr class="hover:bg-gray-50 cursor-pointer" @click="toggleExpand(entry)">
            <td class="pl-3 pr-2 py-2 text-gray-400 font-mono align-top">{{ idx + 1 }}</td>
            <td class="px-2 py-2 align-top">
              <button class="sensitive text-xs font-medium text-blue-600 hover:underline truncate block max-w-full" data-spoof-kind="practice" @click.stop="drillToEntity(entry)">{{ entry.entity_name }}</button>
            </td>
            <td class="px-2 py-2 text-right align-top text-gray-500 tabular-nums">{{ formatPanel(entry.panel_size) }}</td>
            <td class="px-2 py-2 align-top">
              <div class="flex flex-wrap gap-1">
                <span v-for="tag in (entry.structural_tags || [])" :key="tag"
                  :class="structuralBadgeClass(tag)"
                  class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight">
                  {{ formatSlug(tag) }}
                </span>
                <span v-for="d in topDiagnostics(entry)" :key="d.id"
                  :class="diagnosticBadgeClass(d.id)"
                  class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight">
                  {{ formatSlug(d.id) }}
                </span>
              </div>
            </td>
            <td class="px-2 py-2 text-[10px] text-gray-600 align-top leading-snug">{{ analysisSnippet(entry) }}</td>
            <td class="px-2 py-2 text-center align-top">
              <svg v-if="sparkTrajectory(entry.trajectory).length >= 2" width="48" height="20" class="inline-block">
                <line x1="2" :y1="sparkZeroY(sparkTrajectory(entry.trajectory), 20)" x2="46" :y2="sparkZeroY(sparkTrajectory(entry.trajectory), 20)" stroke="#e5e7eb" stroke-width="0.5" />
                <polyline :points="sparkPoints(sparkTrajectory(entry.trajectory), 48, 20)" fill="none" :stroke="sparkColor(sparkTrajectory(entry.trajectory))" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span v-else class="text-[10px] text-gray-300">—</span>
            </td>
            <td class="pr-3 pl-2 py-2 text-right align-top font-semibold text-gray-900">{{ entry.composite_score ?? '—' }}</td>
          </tr>

          <!-- Expanded drawer -->
          <tr v-if="expandedId === entry.entity_id" @click.stop>
            <td colspan="7" class="px-3 pb-3 pt-1">
              <div class="border border-gray-200 rounded-lg bg-gray-50/50 text-[11px] leading-relaxed text-gray-700 divide-y divide-gray-200">

                <!-- Blocker callout -->
                <div v-if="entry.blocker" class="bg-amber-50 px-4 py-2.5 text-amber-800 rounded-t-lg">
                  <span class="font-semibold text-amber-900">Blocker</span>
                  <p class="mt-0.5">{{ entry.blocker }}</p>
                </div>

                <!-- Diagnostics with evidence -->
                <div v-if="entry.diagnostics?.length" class="px-4 py-2.5">
                  <span class="font-semibold text-gray-900 text-[10px] uppercase tracking-wider">Findings</span>
                  <div class="mt-1.5 grid gap-x-3 gap-y-2" style="grid-template-columns: max-content 1fr">
                    <template v-for="d in entry.diagnostics" :key="d.id">
                      <span :class="diagnosticBadgeClass(d.id)"
                        class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight self-start mt-0.5 whitespace-nowrap">
                        {{ formatSlug(d.id) }}
                      </span>
                      <span class="text-gray-600">{{ d.evidence }}</span>
                    </template>
                  </div>
                </div>

                <!-- Trace summary -->
                <div v-if="entry.trace_summary" class="px-4 py-2.5">
                  <span class="font-semibold text-gray-900 text-[10px] uppercase tracking-wider">Assessment</span>
                  <p class="mt-1 text-gray-700">{{ entry.trace_summary }}</p>
                </div>

                <!-- Key quotes -->
                <div v-if="entry.key_quotes?.length" class="px-4 py-2.5">
                  <span class="font-semibold text-gray-900 text-[10px] uppercase tracking-wider">Quotes</span>
                  <ul class="mt-2 space-y-1.5">
                    <li v-for="(q, i) in entry.key_quotes" :key="i" class="flex">
                      <span class="inline-block bg-gray-100 text-gray-700 italic text-[11px] leading-snug px-3 py-2 rounded-2xl rounded-tl-sm max-w-[90%]">{{ '&quot;' + (q) + '&quot;' }}</span>
                    </li>
                  </ul>
                </div>

                <!-- Engagement -->
                <div v-if="entry.engagement_summary" class="px-4 py-2.5">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-gray-900 text-[10px] uppercase tracking-wider">Engagement</span>
                    <span class="text-gray-400">{{ engagementLine(entry.engagement_summary) }}</span>
                  </div>
                  <div v-if="entry.engagement_summary.themes?.length" class="mt-1.5 space-y-1.5">
                    <div v-for="(t, i) in entry.engagement_summary.themes" :key="i">
                      <span class="text-gray-900 font-medium">{{ t.topic }}</span>
                      <p v-if="t.quote" class="mt-0.5 pl-2.5 border-l-2 border-gray-300 text-gray-500 italic">{{ '&quot;' + (t.quote) + '&quot;' }}</p>
                    </div>
                  </div>
                </div>

              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios'

const LIST_TYPE_CONFIG = {
  top_performers:    { title: 'Top Performers',    icon: 'trophy',          color: 'text-amber-500', sort: 'desc' },
  bottom_performers: { title: 'Bottom Performers', icon: 'arrow-trend-down', color: 'text-red-400',   sort: 'asc' },
  blocked_practices: { title: 'Blocked Practices', icon: 'road-barrier',    color: 'text-amber-600', filter: 'blocked' },
}

export default {
  name: 'PracticePerformanceList',
  props: {
    listType:   { type: String, required: true },
    contractId: { type: String, default: null },
    entityId:   { type: Number, default: null },
    year:       { type: Number, default: null },
    limit:      { type: Number, default: 10 },
  },
  data() {
    return { loading: false, entries: [], expandedId: null }
  },
  computed: {
    config() { return LIST_TYPE_CONFIG[this.listType] || { title: this.listType, icon: 'list', color: 'text-gray-400', sort: 'desc' } },
    displayTitle() { return this.config.title },
    listIcon()     { return this.config.icon },
    iconColor()    { return this.config.color },
    displayEntries() {
      // Agent-ranked — render in server order, no client sorting
      return this.entries.slice(0, this.limit)
    },
  },
  watch: {
    listType()   { this.fetchData() },
    contractId() { this.fetchData() },
    year()       { this.fetchData() },
  },
  mounted() { this.fetchData() },
  methods: {
    async fetchData() {
      if (!this.contractId) return
      this.loading = true
      try {
        const params = { contract_id: this.contractId, list_type: this.listType }
        const response = await axios.get('/api/personal-analyst/practice-performance-lists/', { params })
        this.entries = response.data?.entries || []
      } catch (error) {
        console.error('Error fetching practice performance list:', error)
        this.entries = []
      } finally {
        this.loading = false
      }
    },
    toggleExpand(entry) {
      this.expandedId = this.expandedId === entry.entity_id ? null : entry.entity_id
    },
    drillToEntity(entry) {
      if (entry.entity_id) this.$emit('drill-entity', { entity_id: entry.entity_id, entity_name: entry.entity_name })
    },
    topDiagnostics(entry) {
      if (!entry.diagnostics?.length) return []
      return entry.diagnostics.filter(d => d.confidence === 'high').slice(0, 2)
    },
    engagementLine(e) {
      if (!e) return ''
      const parts = [`${e.total_engagements || 0} total`]
      if (e.last_90d_engagements) parts.push(`${e.last_90d_engagements} in last 90 days`)
      if (e.status) parts.push(e.status)
      return parts.join(', ') + '.'
    },
    analysisSnippet(entry) {
      if (entry.headline) return entry.headline
      const trace = entry.trace_summary
      if (!trace) return ''
      const sentence = trace.match(/^[^.!?]+[.!?]/)
      return sentence ? sentence[0] : trace.substring(0, 120)
    },
    sparkTrajectory(trajectory) {
      if (!trajectory?.length) return []
      const seen = new Set()
      return trajectory
        .filter(p => { if (seen.has(p.date)) return false; seen.add(p.date); return true })
        .sort((a, b) => a.date.localeCompare(b.date))
    },
    sparkDomain(points) {
      const vals = points.map(p => (p.score ?? 50) - 50)
      const min = Math.min(...vals, 0)
      const max = Math.max(...vals, 0)
      const span = Math.max(max - min, 4) // at least 4 so flat lines don't fill entire height
      const margin = span * 0.15
      return { min: min - margin, max: max + margin }
    },
    sparkPoints(points, w, h) {
      if (!points?.length || points.length < 2) return ''
      const pad = 2
      const { min: minD, max: maxD } = this.sparkDomain(points)
      return points.map((p, i) => {
        const x = pad + (i / (points.length - 1)) * (w - 2 * pad)
        const val = (p.score ?? 50) - 50
        const y = pad + (1 - (val - minD) / (maxD - minD)) * (h - 2 * pad)
        return `${x.toFixed(1)},${y.toFixed(1)}`
      }).join(' ')
    },
    sparkZeroY(points, h) {
      if (!points?.length) return h / 2
      const pad = 2
      const { min: minD, max: maxD } = this.sparkDomain(points)
      return pad + (1 - (0 - minD) / (maxD - minD)) * (h - 2 * pad)
    },
    sparkColor(points) {
      if (!points?.length) return '#6b7280'
      const lastScore = (points[points.length - 1].score ?? 50) - 50
      if (lastScore > 3) return '#059669'
      if (lastScore < -3) return '#ef4444'
      return '#6b7280'
    },
    formatPanel(size) {
      if (!size) return '—'
      return size >= 1000 ? (size / 1000).toFixed(1) + 'k' : size.toString()
    },
    formatSlug(slug) {
      if (!slug) return ''
      return slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    structuralBadgeClass(tag) {
      const map = {
        fqhc:                    'bg-teal-100 text-teal-700',
        rural_cah:               'bg-teal-100 text-teal-700',
        health_system:           'bg-slate-100 text-slate-600',
        concierge:               'bg-slate-100 text-slate-600',
        specialty_primary_care:  'bg-slate-100 text-slate-600',
        nursing_home_attending:  'bg-slate-100 text-slate-600',
        solo_provider:           'bg-gray-100 text-gray-500',
        multi_provider:          'bg-gray-100 text-gray-500',
        large_panel:             'bg-gray-100 text-gray-500',
        small_panel:             'bg-gray-100 text-gray-500',
      }
      return map[tag] || 'bg-gray-100 text-gray-600'
    },
    diagnosticBadgeClass(id) {
      const map = {
        engagement_momentum:     'bg-emerald-100 text-emerald-800',
        recapture_signal:        'bg-sky-100 text-sky-700',
        claims_catchup:          'bg-blue-100 text-blue-700',
        data_masking:            'bg-blue-100 text-blue-700',
        discipline_loss:         'bg-red-100 text-red-700',
        acceptance_mode:         'bg-red-100 text-red-700',
        visit_conversion_gap:    'bg-orange-100 text-orange-700',
        staffing_crisis:         'bg-amber-100 text-amber-700',
        panel_volatility:        'bg-amber-100 text-amber-700',
        complex_population:      'bg-purple-100 text-purple-700',
        competitive_exit_risk:   'bg-rose-100 text-rose-700',
      }
      return map[id] || 'bg-gray-100 text-gray-600'
    },
  },
}
</script>
